import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { Button } from "components/common/Button";
import { InlineSwitcher } from "components/common/InlineSwitcher";
import { FolderSuggestionTable } from "../FolderSuggestionTable";
import { buildFolderDrafts, buildFolderSuggestionTable } from "../utils";
import type { FolderStructure } from "../types";
import type { FolderStructureChooserProps } from "./types";

export const FolderStructureChooser = ({
  suggestion,
  structures,
  structure,
  planNameById,
  onStructureChange,
  onNext,
  onCancel,
}: FolderStructureChooserProps) => {
  const { t } = useTranslation();

  const table = useMemo(
    () => buildFolderSuggestionTable({ suggestion, structure }),
    [suggestion, structure],
  );

  const folderCount = useMemo(
    () => buildFolderDrafts({ suggestion, structure }).folders.length,
    [suggestion, structure],
  );

  const structureOptions = useMemo(
    () =>
      structures.map((option) => ({
        value: option,
        label: t(`smartSurveyor.folders.suggestion.structures.${option}`),
      })),
    [structures, t],
  );

  return (
    <>
      <div className="mb-4 flex flex-shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <InlineSwitcher<FolderStructure>
          value={structure}
          onChange={onStructureChange}
          options={structureOptions}
          variant="segmented"
          className="w-fit max-w-full"
          listClassName="text-sm"
          itemClassName="whitespace-nowrap px-4"
        />
        <span className="text-sm text-gray-600">
          <Trans
            i18nKey="smartSurveyor.folders.suggestion.createsFolders"
            count={folderCount}
            values={{
              rule: t(
                `smartSurveyor.folders.suggestion.folderRules.${structure}`,
              ),
            }}
            components={{ b: <span className="font-semibold text-blue-700" /> }}
          />
        </span>
      </div>
      <FolderSuggestionTable
        table={table}
        structure={structure}
        planNameById={planNameById}
      />
      <div className="mt-4 flex flex-shrink-0 flex-wrap items-center justify-end gap-3 border-t border-gray-200 pt-3">
        <span className="mr-auto text-sm text-gray-500">
          {t("smartSurveyor.folders.suggestion.nothingChanges")}
        </span>
        <Button onClick={onCancel}>{t("smartSurveyor.common.cancel")}</Button>
        <Button
          variant="primary"
          onClick={onNext}
          endAdornment={<ArrowRight size={16} />}
        >
          {t("smartSurveyor.folders.suggestion.next")}
        </Button>
      </div>
    </>
  );
};
